<template>
    <div class="modal fade" :id="modalId" data-bs-backdrop="static" aria-labelledby="report-page-filter-proposal-modal-title" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header border-bottom-0 pb-0">
                    <div class="d-flex align-items-start w-100">
                        <div class="flex-grow-1" style="min-width: 0; margin-right: 1rem;">
                            <h3 class="mt-2 mb-2 text-center text-sm-start">
                                Filter Suggestion
                            </h3>
                        </div>
                        <div class="d-flex align-items-center" style="flex: 0 0 auto;">
                            <button type="button" class="btn btn-white close-modal" data-bs-dismiss="modal"
                                data-bs-toggle="tooltip" title="Close"
                                @click="handleClose">
                                <img class="icon me-0" src="/img/icons/avid-icons/close.svg" alt="">
                            </button>
                        </div>
                    </div>
                </div>
                <div class="modal-body">
                    <div v-if="loading" class="text-center py-4">
                        <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="mt-2">Loading suggestion...</p>
                    </div>

                    <div v-else-if="error" class="alert alert-danger">
                        {{ error }}
                    </div>

                    <div v-else class="d-flex flex-column gap-3">
                        <div v-if="proposal" class="d-flex flex-column gap-3">
                            <!-- Card: Suggested filter (layout like ReportBuilderUdfPopup) -->
                            <div class="card segment-proposal-card">
                                <div class="card-body p-3">
                                    <h4 class="h6 fw-semibold mb-3">Suggested Filter</h4>
                                    <div class="row mb-3">
                                        <div class="col-12 col-md-6">
                                            <label class="form-label text-muted small mb-0">Group</label>
                                            <p class="mb-0">{{ proposal.filter_group }}</p>
                                        </div>
                                        <div class="col-12 col-md-6">
                                            <label class="form-label text-muted small mb-0">Database Column</label>
                                            <p class="mb-0">{{ formattedColumnName }}</p>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-12 col-md-6">
                                            <label class="form-label text-muted small mb-0">Type</label>
                                            <p class="mb-0">{{ fieldTypeLabel }}</p>
                                        </div>
                                        <div v-if="defaultValueAsText" class="col-12 col-md-6">
                                            <label class="form-label text-muted small mb-0">Default Value</label>
                                            <p class="mb-0">{{ defaultValueAsText }}</p>
                                        </div>
                                    </div>
                                    <div class="mb-0">
                                        <label class="form-label text-muted small mb-1">Filter to be Added</label>
                                        <div v-if="optionsError" class="small text-danger">{{ optionsError }}</div>
                                        <div v-else-if="canShowFilterDropdown" class="filter-proposal-preview">
                                            <advanced-filter-dropdown
                                                :key="'preview-' + (proposalOptions.length || 0) + '-' + (proposal?.field_type || '')"
                                                :filter="previewFilter"
                                                :if_allow_delete="false"
                                                @updateFilter="() => {}"
                                                @searchInLongOptionList="() => {}"
                                            />
                                        </div>
                                        <div v-else-if="optionsLoading" class="text-muted small">Loading options...</div>
                                        <div v-else class="text-muted small">{{ proposal.label }}</div>
                                    </div>

                                </div>
                            </div>
                            <!-- Filter Logic: accordion card, collapsed by default (new metric filter only) -->
                            <div v-if="isNewMetricFilter" class="card segment-proposal-card">
                                <div class="accordion accordion-flush" :id="`${modalId}-accordion-filter-logic`">
                                    <div class="accordion-item border-0 bg-white">
                                        <h2 class="accordion-header">
                                            <button
                                                class="accordion-button collapsed py-3 h6 fw-semibold mb-0"
                                                type="button"
                                                data-bs-toggle="collapse"
                                                :data-bs-target="`#${modalId}-collapse-filter-logic`"
                                                aria-expanded="false"
                                                :aria-controls="`${modalId}-collapse-filter-logic`"
                                            >
                                                Filter Logic
                                            </button>
                                        </h2>
                                        <div
                                            :id="`${modalId}-collapse-filter-logic`"
                                            class="accordion-collapse collapse"
                                            :data-bs-parent="`#${modalId}-accordion-filter-logic`"
                                        >
                                            <div class="accordion-body p-3 pt-0">
                                                <div v-if="newMetricFilterDescription" class="metric-logic-description text-muted" v-html="newMetricFilterDescription"></div>
                                                <div v-else-if="newMetricFilterFormula" class="metric-logic-formula">
                                                    <code class="d-block p-2 rounded bg-light text-dark">{{ newMetricFilterFormula }}</code>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-show="proposal.reasoning" class="card segment-proposal-card">
                                <div class="card-body p-3">
                                    <h4 class="h6 fw-semibold mb-0"><img class="icon me-2" src="/img/icons/avid-icons/edna.svg" alt="">Reasoning</h4>
                                    <div class="text-muted mt-2" style="white-space: pre-line;">{{ proposal.reasoning }}</div>
                                </div>
                            </div>
                        </div>
                        <p v-else class="text-muted">No suggestion available.</p>
                        <div v-if="proposal && page?.client_id" class="form-check mt-2">
                            <input
                                id="apply-to-similar-pages"
                                v-model="applyToSimilarPages"
                                type="checkbox"
                                class="form-check-input"
                             aria-label="Apply To Similar Pages">
                            <label class="form-check-label" for="apply-to-similar-pages">
                                Apply filter to all similar report pages
                            </label>
                            <div class="form-text small text-muted">
                                Adds this filter to every {{ page?.data_blend_type || 'matching' }} report page that has the same filter group. Pages that already have this column or metric filter in that group are skipped.
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer pt-1">
                    <button
                        v-if="!loading && !error"
                        type="button"
                        class="btn btn-secondary"
                        @click="submitRefine"
                        :disabled="submitting"
                    >
                        <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        <img v-else class="icon me-2" src="/img/icons/avid-icons/edna.svg" alt="">
                        Refine
                    </button>
                    <button
                        v-if="!loading && !error && proposal"
                        type="button"
                        class="btn btn-primary reg-btn"
                        @click="acceptProposal"
                        :disabled="submitting"
                    >
                        <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        Accept
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';
import AdvancedFilterDropdown from '../components/AdvancedFilterDropdown.vue';
import { useNotifications } from '../../composables/useNotifications';

export default defineComponent({
    name: 'ReportPageFilterProposalModal',
    components: { AdvancedFilterDropdown },
    props: {
        suggestionId: {
            type: [Number, String],
            default: null,
        },
        client: {
            type: Object,
            required: true,
        },
        page: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            modalId: `report-page-filter-proposal-modal-${Date.now()}`,
            loading: false,
            error: null,
            proposal: null,
            submitting: false,
            currentSuggestionId: null,
            loadedSuggestion: null,
            monthOrder: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
            proposalOptions: [],
            optionsLoading: false,
            optionsError: null,
            sliderMin: 0,
            sliderMax: 100,
            applyToSimilarPages: false,
        };
    },
    computed: {
        dataBlendId() {
            const p = this.page;
            if (!p) return null;
            return (p.data_blend && p.data_blend.id) || p.data_blend_id || null;
        },
        needsOptions() {
            if (!this.proposal) return false;
            const t = this.proposal.field_type;
            return t === 'single_select' || t === 'multi_select' || t === 'array_single_select' || t === 'array_multi_select' || t === 'month_picker';
        },
        needsMinMax() {
            return this.proposal && this.proposal.field_type === 'numeric_slider';
        },
        displayOptions() {
            if (this.optionsLoading || this.optionsError) return [];
            if (this.proposalOptions && this.proposalOptions.length) return this.proposalOptions;
            return this.fallbackOptions;
        },
        fallbackOptions() {
            if (!this.proposal) return [];
            const dv = this.proposal.default_value;
            if (this.proposal.field_type === 'single_select' && dv != null && typeof dv === 'string') {
                return [dv, 'Option A', 'Option B', 'Option C'];
            }
            if ((this.proposal.field_type === 'multi_select' || this.proposal.field_type === 'array_single_select' || this.proposal.field_type === 'array_multi_select') && Array.isArray(dv) && dv.length) {
                return [...dv, 'Option A', 'Option B'].slice(0, 5);
            }
            return [];
        },
        mockSelectedSingle() {
            if (!this.proposal || (this.proposal.field_type !== 'single_select' && this.proposal.field_type !== 'array_single_select')) return null;
            const dv = this.proposal.default_value;
            const opts = this.displayOptions;
            if (this.proposal.field_type === 'array_single_select') {
                return Array.isArray(dv) && dv.length ? dv[0] : (opts[0] ?? this.proposal.label);
            }
            return (dv != null && typeof dv === 'string') ? dv : (opts[0] ?? this.proposal.label);
        },
        mockSelectedMulti() {
            if (!this.proposal || (this.proposal.field_type !== 'multi_select' && this.proposal.field_type !== 'array_multi_select')) return [];
            const dv = this.proposal.default_value;
            const opts = this.displayOptions;
            return Array.isArray(dv) ? dv.slice(0, 2) : (opts.length ? [opts[0]] : []);
        },
        monthPickerOptions() {
            if (this.proposal && this.proposal.field_type === 'month_picker' && this.displayOptions.length) {
                const order = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                return [...this.displayOptions].sort((a, b) => order.indexOf(a) - order.indexOf(b));
            }
            return this.monthOrder;
        },
        sliderPreviewValue() {
            return Math.round((this.sliderMin + this.sliderMax) / 2);
        },
        formattedColumnName() {
            if (!this.proposal) return '—';
            if (this.proposal.column == null || this.proposal.column === '') {
                return (this.proposal.new_metric_filter && this.proposal.new_metric_filter.name)
                    ? this.proposal.new_metric_filter.name
                    : 'New Custom Metric';
            }
            const col = typeof this.proposal.column === 'object' && this.proposal.column != null
                ? this.proposal.column.value ?? this.proposal.column.text
                : this.proposal.column;
            const s = String(col).trim();
            if (/^\d+$/.test(s)) return 'Custom';
            const parts = s.split('.').map((part) => {
                const withSpaces = part.replace(/_/g, ' ');
                return withSpaces.replace(/\b\w/g, (c) => c.toUpperCase());
            });
            return parts.join(': ');
        },
        fieldTypeLabel() {
            if (!this.proposal || !this.proposal.field_type) return '—';
            const labels = {
                single_select: 'Single Select',
                multi_select: 'Multiple Select',
                array_single_select: 'Single Select - Filters by Account',
                array_multi_select: 'Multiple Select - Filters by Account',
                date_range: 'Date Range',
                month_range: 'Month Range',
                month_picker: 'Month Picker',
                numeric_slider: 'Numeric Slider',
            };
            return labels[this.proposal.field_type] || this.proposal.field_type;
        },
        defaultValueAsText() {
            if (!this.proposal || this.proposal.default_value == null) return null;
            const dv = this.proposal.default_value;
            if (Array.isArray(dv)) return dv.length ? dv.join(', ') : null;
            if (typeof dv === 'object' && dv !== null) {
                if (dv.start_date != null || dv.end_date != null) {
                    return [dv.start_date, dv.end_date].filter(Boolean).join(' — ');
                }
                if (dv.start != null || dv.end != null) return [dv.start, dv.end].filter(Boolean).join(' — ');
            }
            return String(dv);
        },
        canShowFilterDropdown() {
            if (!this.proposal) return false;
            const t = this.proposal.field_type;
            if (t === 'single_select' || t === 'multi_select' || t === 'array_single_select' || t === 'array_multi_select' || t === 'month_picker') {
                return !this.optionsLoading && this.displayOptions.length > 0;
            }
            if (t === 'numeric_slider') return !this.optionsLoading;
            if (t === 'date_range' || t === 'month_range') return true;
            return false;
        },
        isNewMetricFilter() {
            return this.proposal && this.proposal.new_metric_filter && (this.proposal.new_metric_filter.formula || this.proposal.new_metric_filter.description);
        },
        newMetricFilterDescription() {
            if (!this.proposal?.new_metric_filter?.description) return null;
            const d = this.proposal.new_metric_filter.description;
            return typeof d === 'string' && d.trim() ? d.trim() : null;
        },
        newMetricFilterFormula() {
            if (!this.proposal?.new_metric_filter?.formula) return null;
            const f = this.proposal.new_metric_filter.formula;
            return typeof f === 'string' && f.trim() ? f.trim() : null;
        },
        previewFilter() {
            if (!this.proposal) return null;
            const p = this.proposal;
            const dv = p.default_value;
            const base = {
                id: 'preview',
                label: p.label,
                field_type: p.field_type,
                column: p.column,
                is_required: false,
                options: this.displayOptions.length ? [...this.displayOptions] : (p.field_type === 'month_picker' ? this.monthOrder : []),
                value: undefined,
                default_value: undefined,
            };
            if (p.field_type === 'single_select') {
                base.value = (dv != null && typeof dv === 'string') ? dv : (this.displayOptions[0] ?? '');
                base.default_value = base.value;
            } else if (p.field_type === 'array_single_select') {
                base.value = Array.isArray(dv) ? [...dv] : [];
                base.default_value = base.value;
            } else if (p.field_type === 'multi_select' || p.field_type === 'array_multi_select' || p.field_type === 'month_picker') {
                base.value = Array.isArray(dv) ? [...dv] : [];
                base.default_value = base.value;
            } else if (p.field_type === 'numeric_slider') {
                base.slider_min = this.sliderMin;
                base.slider_max = this.sliderMax;
                base.value = [this.sliderMin, this.sliderMax];
                base.default_value = base.value;
            } else if (p.field_type === 'date_range' || p.field_type === 'month_range') {
                base.value = (dv && typeof dv === 'object') ? { ...dv } : { start_date: '', end_date: '' };
                base.default_value = base.value;
            }
            return base;
        },
    },
    mounted() {
        const { showNotification } = useNotifications();
        this.showNotification = showNotification;
    },
    methods: {
        async loadProposal() {
            if (!this.currentSuggestionId) {
                this.proposal = null;
                this.loading = false;
                return;
            }
            this.loading = true;
            this.error = null;
            this.proposalOptions = [];
            this.optionsLoading = false;
            this.optionsError = null;
            this.sliderMin = 0;
            this.sliderMax = 100;
            try {
                const res = await window.axios.get(`/api/insights/filter-suggestions/${this.currentSuggestionId}`);
                this.loadedSuggestion = res.data.suggestion || null;
                this.proposal = res.data.proposal || (this.loadedSuggestion && this.loadedSuggestion.result) || null;
                if (this.proposal && res.data.reasoning) {
                    this.proposal = { ...this.proposal, reasoning: res.data.reasoning };
                }
                const hasColumn = this.proposal && this.proposal.column != null && this.proposal.column !== '';
                const hasNewMetricFilter = this.proposal && this.proposal.new_metric_filter && this.proposal.new_metric_filter.formula;
                if (hasColumn || hasNewMetricFilter) {
                    if (this.needsOptions) {
                        this.loadProposalOptions();
                    } else if (this.needsMinMax && this.dataBlendId) {
                        this.loadProposalMinMax();
                    }
                }
            } catch (err) {
                console.error('ReportPageFilterProposalModal: load error', err);
                this.error = err.response?.data?.message || 'Failed to load suggestion.';
                this.proposal = null;
            } finally {
                this.loading = false;
            }
        },
        loadProposalOptions() {
            if (!this.proposal || !this.client) return;
            this.proposalOptions = [];
            this.optionsError = null;
            this.optionsLoading = true;
            const column = typeof this.proposal.column === 'object' && this.proposal.column != null
                ? this.proposal.column.value
                : this.proposal.column;
            const columnStr = column != null ? String(column).trim() : '';
            const isMetricFilterId = /^\d+$/.test(columnStr);
            const hasNewMetricFilter = this.proposal.new_metric_filter && this.proposal.new_metric_filter.formula;

            if ((isMetricFilterId || hasNewMetricFilter) && this.currentSuggestionId) {
                window.axios.get(`/api/insights/filter-suggestions/${this.currentSuggestionId}/options`)
                    .then((response) => {
                        const options = response.data.options || [];
                        this.proposalOptions = options.slice(0, 1000);
                        if (this.proposal.field_type === 'month_picker') {
                            const order = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                            this.proposalOptions.sort((a, b) => order.indexOf(a) - order.indexOf(b));
                        } else {
                            this.proposalOptions.sort((a, b) => (String(a) > String(b) ? 1 : -1));
                        }
                    })
                    .catch((err) => {
                        console.error('ReportPageFilterProposalModal: load options error', err);
                        this.optionsError = err.response?.data?.message || 'Could not load options.';
                    })
                    .finally(() => {
                        this.optionsLoading = false;
                    });
                return;
            }

            if (!this.dataBlendId || columnStr === '') {
                this.optionsLoading = false;
                return;
            }
            const client = typeof this.client === 'object' && this.client != null ? { id: this.client.id } : { id: this.client };
            window.axios.post('/api/bigquery/get_samples', {
                source: 'data_blend',
                column: columnStr,
                limit: 1000,
                data_source_id: this.dataBlendId,
                staging_table: false,
                client,
            }).then((response) => {
                const samples = response.data.samples || [];
                const options = [];
                for (let i = 0; i < samples.length; i++) {
                    if (samples[i].name != null) {
                        options.push(samples[i].name);
                    }
                }
                if (this.proposal.field_type === 'month_picker') {
                    const order = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    options.sort((a, b) => order.indexOf(a) - order.indexOf(b));
                } else {
                    options.sort((a, b) => (String(a) > String(b) ? 1 : -1));
                }
                this.proposalOptions = options;
            }).catch((err) => {
                console.error('ReportPageFilterProposalModal: load options error', err);
                this.optionsError = err.response?.data?.message || 'Could not load options.';
                this.proposalOptions = [];
            }).finally(() => {
                this.optionsLoading = false;
            });
        },
        loadProposalMinMax() {
            if (!this.dataBlendId || !this.proposal || !this.client) return;
            this.optionsError = null;
            this.optionsLoading = true;
            const column = typeof this.proposal.column === 'object' && this.proposal.column != null
                ? this.proposal.column.value
                : this.proposal.column;
            const client = typeof this.client === 'object' && this.client != null ? { id: this.client.id } : { id: this.client };
            window.axios.post('/api/bigquery/get_min_max', {
                source: 'data_blend',
                column,
                data_source_id: this.dataBlendId,
                client,
            }).then((response) => {
                const data = response.data;
                if (typeof data === 'object' && data.min !== undefined && data.max !== undefined) {
                    this.sliderMin = Math.floor(data.min);
                    this.sliderMax = Math.ceil(data.max);
                } else if (Array.isArray(data) && data.length) {
                    const values = data.map((r) => (r && (r.name != null || r.value != null) ? Number(r.name ?? r.value) : NaN)).filter((n) => !isNaN(n));
                    if (values.length) {
                        this.sliderMin = Math.floor(Math.min(...values));
                        this.sliderMax = Math.ceil(Math.max(...values));
                    }
                }
            }).catch((err) => {
                console.error('ReportPageFilterProposalModal: load min/max error', err);
                this.optionsError = err.response?.data?.message || 'Could not load range.';
            }).finally(() => {
                this.optionsLoading = false;
            });
        },
        async acceptProposal() {
            if (!this.proposal || !this.currentSuggestionId) return;
            this.submitting = true;
            try {
                const res = await window.axios.post(`/api/insights/filter-suggestions/${this.currentSuggestionId}/accept`, {
                    apply_to_similar_pages: this.applyToSimilarPages,
                });
                const msg = res.data?.added_to_similar_count != null
                    ? `Filter added successfully. Also added to ${res.data.added_to_similar_count} similar report(s).`
                    : 'Filter added successfully.';
                this.showNotification({ text: msg, type: 'success' });
                const modalEl = document.getElementById(this.modalId);
                if (modalEl && window.bootstrap) {
                    const modal = window.bootstrap.Modal.getInstance(modalEl);
                    if (modal) modal.hide();
                }
                this.$emit('accepted', res.data);
            } catch (err) {
                this.showNotification({
                    text: err.response?.data?.message || 'Failed to accept filter. Please try again.',
                    type: 'error',
                });
            } finally {
                this.submitting = false;
            }
        },
        submitRefine() {
            Swal.fire({
                title: 'Refine Filter Suggestion',
                target: document.getElementById(this.modalId),
                html: `
                    <p class="mb-2">How can we improve the current filter suggestion?</p>
                    <textarea id="report-filter-refine-input" class="form-control" placeholder="E.g., Use a different column; Change the label; etc." style="width: 100%; min-height: 100px;"></textarea>
                `,
                showCancelButton: true,
                confirmButtonText: 'Submit',
                cancelButtonText: 'Cancel',
                preConfirm: () => {
                    const el = document.getElementById('report-filter-refine-input');
                    return el && el.value ? el.value.trim() : null;
                },
            }).then(async (result) => {
                if (result.isConfirmed && result.value && this.currentSuggestionId) {
                    this.submitting = true;
                    try {
                        await window.axios.post(`/api/insights/filter-suggestions/${this.currentSuggestionId}/refine`, {
                            user_feedback: result.value,
                            previous_response_id: this.loadedSuggestion?.openai_response_id || undefined,
                        });
                        this.showNotification({
                            icon: '/img/icons/nav-icons/Edna.svg',
                            text: 'We\'re refining the suggestion. You\'ll be notified when it\'s ready!',
                            type: 'info',
                        });
                        const modalEl = document.getElementById(this.modalId);
                        if (modalEl && window.bootstrap) {
                            const modal = window.bootstrap.Modal.getInstance(modalEl);
                            if (modal) modal.hide();
                        }
                        this.$emit('refined');
                    } catch (err) {
                        this.showNotification({
                            text: err.response?.data?.message || 'Failed to submit feedback. Please try again.',
                            type: 'error',
                        });
                    } finally {
                        this.submitting = false;
                    }
                }
            });
        },
        show(suggestionId) {
            this.currentSuggestionId = suggestionId;
            this.proposal = null;
            this.error = null;
            this.loadProposal();
            const modalEl = document.getElementById(this.modalId);
            if (modalEl && window.bootstrap) {
                let modal = window.bootstrap.Modal.getInstance(modalEl);
                if (!modal) modal = new window.bootstrap.Modal(modalEl);
                modal.show();
            }
        },
        handleClose() {
            this.currentSuggestionId = null;
        },
    },
});
</script>
